// Per-member progress through one assigned track, for the coach's active group.
//
// Reported in COURSES, not lessons. A Kingdom Factor course almost always
// carries a single member-visible lesson, so a lesson progress bar would be a
// boolean drawn wide. Each course is a check; the row-level checklist strip is
// the track. Lesson counts surface only inside a multi-lesson course.
import { Head, Link, router } from "@inertiajs/react"
import { useMemo, useState } from "react"
import { ArrowLeft, Award, Layers, Check, ChevronDown, ChevronRight, MessageSquare, Users } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { TrackAnswersDialog } from "./TrackAnswersDialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"

function ratio(member) {
  return member.coursesTotal > 0 ? member.coursesCompleted / member.coursesTotal : 0
}

function statusOf(member) {
  if (member.coursesTotal > 0 && member.coursesCompleted === member.coursesTotal) return "complete"
  return member.started ? "partial" : "none"
}

function relativeTime(iso) {
  if (!iso) return null
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  return `${Math.floor(days / 365)} years ago`
}

function StatusCell({ member }) {
  const status = statusOf(member)
  if (status === "complete") {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Complete</Badge>
  }
  if (status === "partial") {
    return (
      <span className="text-sm text-zinc-900 tabular-nums">
        {member.coursesCompleted} of {member.coursesTotal}
      </span>
    )
  }
  return (
    <Badge variant="outline" className="text-zinc-500 font-normal">
      Not started
    </Badge>
  )
}

// One square per course, in track order. This is the whole point of the page:
// a coach reads who is behind without opening a single course.
function Checklist({ byCourse, titleFor }) {
  return (
    <div className="flex flex-wrap gap-1">
      {byCourse.map((c) => (
        <span
          key={c.courseId}
          title={`${c.step}. ${titleFor(c.courseId)}${c.complete ? " — complete" : c.completed > 0 ? ` — ${c.completed} of ${c.total} lessons` : " — not started"}`}
          className={
            "h-2.5 w-2.5 rounded-sm " +
            (c.complete ? "bg-kf-red" : c.completed > 0 ? "bg-amber-400" : "bg-zinc-200")
          }
        />
      ))}
    </div>
  )
}

function CourseMark({ course }) {
  if (course.complete) return <Check className="h-4 w-4 text-emerald-600" aria-label="Complete" />
  if (course.completed > 0) {
    return (
      <span className="text-[11px] text-amber-700 tabular-nums">
        {course.completed}/{course.total}
      </span>
    )
  }
  return <span className="block h-1.5 w-1.5 rounded-full bg-zinc-300" aria-label="Not started" />
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <p className="font-display text-2xl text-zinc-900 leading-none">{value}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
    </div>
  )
}

export default function LearningTrackShow({
  currentUser,
  coachGroup,
  track,
  courses,
  members,
  memberAnswers,
}) {
  const [expanded, setExpanded] = useState(() => new Set())
  const [answersFor, setAnswersFor] = useState(null)
  const [sortKey, setSortKey] = useState("progress")
  const [direction, setDirection] = useState("asc")

  const titleFor = useMemo(() => {
    const byId = new Map(courses.map((c) => [c.id, c.title]))
    return (id) => byId.get(id) || "Course"
  }, [courses])

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleSort = (col) => {
    if (sortKey === col) {
      setDirection(direction === "asc" ? "desc" : "asc")
    } else {
      setSortKey(col)
      setDirection("asc")
    }
  }

  const rows = useMemo(() => {
    const sorted = [...members]
    sorted.sort((a, b) => {
      const flip = direction === "asc" ? 1 : -1
      if (sortKey === "name") return flip * a.name.localeCompare(b.name)
      const delta = ratio(a) - ratio(b)
      return delta !== 0 ? flip * delta : a.name.localeCompare(b.name)
    })
    return sorted
  }, [members, sortKey, direction])

  // The answer TEXT is fetched on demand, one member at a time, rather than
  // shipped with every roster load. A JSON endpoint would break ADR-0001.
  const openAnswers = (member) => {
    setAnswersFor(member)
    router.reload({
      only: ["memberAnswers"],
      data: { answers_for: member.id },
      preserveState: true,
      preserveScroll: true,
    })
  }

  const complete = members.filter((m) => statusOf(m) === "complete").length
  const partial = members.filter((m) => statusOf(m) === "partial").length

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${track.title} — Progress`} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link
          href="/app/coach/content?tab=tracks"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Content
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-kf-red shrink-0" />
              <h1 className="font-display text-3xl text-zinc-900">{track.title}</h1>
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              {courses.length} {courses.length === 1 ? "course" : "courses"} · {members.length}{" "}
              {members.length === 1 ? "member" : "members"} in{" "}
              <span className="font-medium text-zinc-700">{coachGroup?.name}</span>
            </p>
          </div>
          {members.length > 0 && (
            <div className="flex gap-6">
              <Stat label="Complete" value={complete} />
              <Stat label="In progress" value={partial} />
              <Stat label="Not started" value={members.length - complete - partial} />
            </div>
          )}
        </div>

        {members.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No members in this group yet"
            body="Add members to the group and their progress through this track will show up here."
          />
        ) : courses.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="Nothing to track yet"
            body="This track has no published courses your members can work through."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <SortableHeader
                      column="name"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                    >
                      Member
                    </SortableHeader>
                    <SortableHeader
                      column="progress"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="w-32"
                    >
                      Courses
                    </SortableHeader>
                    <TableHead className="hidden sm:table-cell">Checklist</TableHead>
                    {track.certifying && <TableHead className="w-28 hidden sm:table-cell">Certified</TableHead>}
                    {/* Plain text, never a SortableHeader: a second ranking
                        axis on a ten-row table is UI nobody asked for. */}
                    <TableHead className="w-24 hidden md:table-cell">Answers</TableHead>
                    <TableHead className="w-32 hidden md:table-cell">Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((member) => {
                    const isOpen = expanded.has(member.id)
                    return [
                      <TableRow
                        key={member.id}
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => toggle(member.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            toggle(member.id)
                          }
                        }}
                      >
                        <TableCell className="text-zinc-400">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-zinc-900">{member.name}</p>
                          <p className="text-xs text-zinc-500">{member.email}</p>
                        </TableCell>
                        <TableCell>
                          <StatusCell member={member} />
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Checklist byCourse={member.byCourse} titleFor={titleFor} />
                        </TableCell>
                        {track.certifying && (
                          <TableCell className="hidden sm:table-cell text-xs">
                            {member.certification ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
                                <Award className="h-3 w-3" />
                                {member.certification.earnedOn}
                              </span>
                            ) : (
                              <span className="text-zinc-400">—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell text-xs text-zinc-500 tabular-nums">
                          {member.answerCount > 0 ? (
                            <>
                              {member.answerCount}{" "}
                              {member.answerCount === 1 ? "answer" : "answers"}
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-zinc-500">
                          {relativeTime(member.lastActivityAt) || "—"}
                        </TableCell>
                      </TableRow>,
                      isOpen && (
                        <TableRow key={`${member.id}-detail`} className="bg-zinc-50 hover:bg-zinc-50">
                          <TableCell />
                          <TableCell colSpan={track.certifying ? 6 : 5} className="py-3">
                            {/* Both affordances live HERE, never in the
                                collapsed row: that row is role="button" with an
                                onClick toggle, so a Link and a Button inside it
                                would need stopPropagation on each plus an
                                interactive-inside-interactive a11y violation. */}
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <Link
                                href={`/app/coach/members/${member.id}?back=tracks`}
                                className="text-sm font-medium text-zinc-900 hover:text-kf-red underline-offset-2 hover:underline"
                              >
                                {member.name}
                              </Link>
                              {/* Always shown, always enabled. With nothing
                                  answered the dialog lists the questions still
                                  waiting, which is useful before a session; a
                                  disabled control recreates the dead end. */}
                              <div className="flex items-center gap-2">
                                {member.certification && (
                                  <Button asChild variant="outline" size="sm">
                                    <a href={member.certification.pdfUrl} target="_blank" rel="noopener noreferrer">
                                      <Award className="h-4 w-4 mr-1.5" />
                                      Certificate
                                    </a>
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openAnswers(member)}
                                >
                                  <MessageSquare className="h-4 w-4 mr-1.5" />
                                  Read answers
                                </Button>
                              </div>
                            </div>
                            {member.byCourse.length === 0 ? (
                              <p className="text-sm text-zinc-500 italic">
                                Nothing in this track is visible to {member.name}.
                              </p>
                            ) : (
                              <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
                                {member.byCourse.map((course) => (
                                  <li
                                    key={course.courseId}
                                    className="flex items-center gap-2 text-sm"
                                  >
                                    <span className="w-5 text-right text-xs text-zinc-400 tabular-nums">
                                      {course.step}.
                                    </span>
                                    <span className="w-6 flex justify-center shrink-0">
                                      <CourseMark course={course} />
                                    </span>
                                    <span
                                      className={
                                        course.complete ? "text-zinc-500" : "text-zinc-800"
                                      }
                                    >
                                      {titleFor(course.courseId)}
                                    </span>
                                  </li>
                                ))}
                              </ol>
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    ]
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <TrackAnswersDialog
        open={!!answersFor}
        onOpenChange={(next) => !next && setAnswersFor(null)}
        answers={memberAnswers}
        member={answersFor}
        trackTitle={track.title}
      />
    </CoachLayout>
  )
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <Card>
      <CardContent className="text-center py-12">
        <Icon className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
        <p className="font-medium text-zinc-900">{title}</p>
        <p className="text-sm text-zinc-500 mt-1">{body}</p>
      </CardContent>
    </Card>
  )
}
